import { useState } from "react";
import {
  PlusIcon,
  EyeIcon,
  TrashIcon,
  PencilSquareIcon,
} from "@heroicons/react/24/outline";
import { Subheading } from "../../../../design/heading";
import { Button } from "../../../../design/button";
import {
  Table,
  TableBody,
  TableCell,
  TableRow,
} from "../../../../design/table";
import { DiscoveryRequestTask } from "../../../../models/DiscoveryRequestTask";
import { CheckBadgeIcon, CheckIcon } from "@heroicons/react/20/solid";
import { useCreateDiscoveryRequestTaskResponseDialog } from "../../../../providers/CreateDiscoveryRequestTaskResponseDialogProvider";
import { useCompleteDiscoveryRequestTaskAlert } from "../../../../providers/CompleteDiscoveryRequestTaskAlertProvider";
import { Tooltip } from "../../../../design/tooltip";
import moment from "moment";
import { useDeleteDiscoveryRequestTaskResponseAlert } from "../../../../providers/DeleteDiscoveryRequestTaskResponseAlertProvider";
import { useDeleteDiscoveryRequestTaskAlert } from "../../../../providers/DeleteDiscoveryRequestTaskAlertProvider";
import { useUpdateDiscoveryRequestTaskDialog } from "../../../../providers/UpdateDiscoveryRequestTaskDialogProvider";
import DownloadDiscoveryRequestTaskResponseButton from "../../../../components/DownloadDiscoveryRequestTaskResponseButton";

interface DiscoveryRequestTaskListItemProps {
  task: DiscoveryRequestTask;
}

const DiscoveryRequestTaskListItem: React.FC<
  DiscoveryRequestTaskListItemProps
> = ({ task }) => {
  const { openDialog } = useCreateDiscoveryRequestTaskResponseDialog();
  const { openAlert } = useCompleteDiscoveryRequestTaskAlert();
  const { openAlert: openDeleteResponseAlert } =
    useDeleteDiscoveryRequestTaskResponseAlert();
  const { openAlert: openDeleteTaskAlert } =
    useDeleteDiscoveryRequestTaskAlert();
  const { openDialog: openUpdateDiscoveryRequestTaskDialog } =
    useUpdateDiscoveryRequestTaskDialog();

  return (
    <div className="p-4 space-y-4 bg-white border rounded-lg shadow-sm">
      {/* Task Header */}
      <div className="flex items-start justify-between">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Subheading className="text-lg font-semibold">
              {task.title || "Untitled Task"}
            </Subheading>
            {task.isCompleted && (
              <Tooltip
                content={`Completed ${moment(task.completedAt ?? Date()).fromNow()}`}
              >
                <CheckBadgeIcon className="w-5 h-5 text-green-500" />
              </Tooltip>
            )}
          </div>
          {task.description && (
            <Subheading className="text-sm text-gray-600">
              {task.description}
            </Subheading>
          )}
        </div>
        <div className="flex items-center gap-2">
          {!task.isCompleted && (
            <Button onClick={() => openAlert(task)}>
              <CheckIcon className="w-4 h-4" />
            </Button>
          )}

          <Button
            plain
            onClick={() => openUpdateDiscoveryRequestTaskDialog(task)}
          >
            <PencilSquareIcon className="w-4 h-4" />
          </Button>
          <Button plain onClick={() => openDeleteTaskAlert(task)}>
            <TrashIcon className="w-4 h-4 text-red-500" />
          </Button>
        </div>
      </div>

      {/* Files Table */}
      {task.responses?.length > 0 && (
        <Table>
          <TableBody>
            {task.responses.map((response) => (
              <TableRow key={response._id}>
                <TableCell>{response.title || "Unnamed File"}</TableCell>
                <TableCell className="space-x-2 text-right">
                  <DownloadDiscoveryRequestTaskResponseButton
                    discoveryRequestTaskResponse={response}
                  />
                  <Button
                    plain
                    onClick={() => openDeleteResponseAlert(response)}
                  >
                    <TrashIcon className="w-4 h-4 text-red-500" />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      {/* Upload File */}
      <div className="flex items-center gap-3">
        <Button onClick={() => openDialog(task)}>
          <>
            <PlusIcon className="w-4 h-4" />
            <span className="hidden sm:block">Add Response</span>
          </>
        </Button>
      </div>
    </div>
  );
};

export default DiscoveryRequestTaskListItem;
